import React from 'react';

import type { BlockCannabisCounselingCta } from 'types/block-cannabis-counseling-cta';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useEventsTracker } from 'contexts/eventsTracker';

import { Wrap, Button, Card, Paragraph } from '@leafwell/components';
import Figure from './figure';
import HeadingBody from './heading-body';

const CannabisCounselingCta: React.FC<
  BlockCannabisCounselingCta & { isBlogPost?: boolean }
> = ({
  variant = 'external',
  title,
  description,
  internalImage,
  externalImage,
  button,
  isBlogPost = false,
}) => {
  const { trackEvent } = useEventsTracker();
  const {
    cannabisGuidanceCta: {
      defaultButtonLink,
      defaultExternalImage,
      defaultInternalImage,
      defaultBlogPostImage,
    },
  } = useWordPressContext();

  const isFull = variant === 'full';

  button = button ? button : defaultButtonLink;
  externalImage = externalImage
    ? externalImage
    : isBlogPost
    ? defaultBlogPostImage
    : defaultExternalImage;
  internalImage = internalImage ? internalImage : defaultInternalImage;

  const Wrapper = isBlogPost ? 'div' : Wrap;

  return (
    <Wrapper
      className={[
        'grid !gap-2.5',
        isBlogPost
          ? 'md:grid-cols-[35%_65%] mt-12 mb-6 not-prose'
          : 'md:grid-cols-[65%_35%]',
        isFull ? 'lg:mt-24' : '',
      ].join(' ')}
    >
      <Card
        rounded={true}
        filled={true}
        variant="secondary"
        className={[isFull ? 'lg:grid-cols-[auto_60%]' : 'min-h-68'].join(' ')}
      >
        {isFull && (
          <Figure
            {...internalImage}
            classNameImage="absolute -bottom-[25px]"
            className="hidden lg:block lg:self-center lg:justify-self-center w-full h-full !overflow-visible lg:max-w-60"
          />
        )}
        <div className={['grid', isBlogPost ? 'gap-3' : 'gap-6'].join(' ')}>
          <HeadingBody
            title={title}
            level="2"
            tagName="h2"
            className="font-semibold"
          />
          <Paragraph
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
          {button && (
            <Button
              href={button.url}
              label={button.title}
              rounded={true}
              variant="light"
              className="md:w-fit h-fit self-end"
              onClick={() => {
                trackEvent({
                  event: 'select_content',
                  content_type: button.title,
                  item_id: 'product_cta',
                  origin: 'window?.location?.href',
                });
              }}
            />
          )}
        </div>
      </Card>
      <Figure
        {...externalImage}
        className={[
          isBlogPost ? 'order-first max-h-72 md:max-h-full' : '',
          'rounded-2xl',
        ].join(' ')}
        classNameImage="md:absolute md:top-0 md:left-0 md:h-full md:object-cover md:object-[50%_40%]"
      />
    </Wrapper>
  );
};

export default CannabisCounselingCta;
